import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
} from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();
  #cartUpdateAbortController = new AbortController();
  #isLoading = false;
  get productPageUrl() {
    const productCard = this.closest('product-card');
    if (productCard) return productCard.productPageUrl;
    const hotspotProduct = this.closest('product-hotspot-component');
    const productLink = hotspotProduct?.getHotspotProductLink();
    return productLink?.href || '';
  }
  #getSelectedVariantId() {
    const productCard = this.closest('product-card');
    return productCard?.getSelectedVariantId() ?? null;
  }
  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      { signal: this.#cartUpdateAbortController.signal },
    );
    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate,
    );
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate,
    );
  }
  /**
   * React to a product-select event from a product card: switch the quick-add
   * button between "add" (single option) and "choose" (multi option), unless
   * the card uses selling plans or renders both buttons.
   *
   * @param {CustomEvent} event
   * @returns {void}
   */ #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card'))
      return;
    if (this.dataset.usesSellingPlans === 'true') return;
    if (this.dataset.rendersBothButtons !== 'true') return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };
  /**
   * Update the variant picker inside the open quick-add modal from the
   * fetched product page HTML (`newHtml` is a parsed Document). No-op when
   * the modal content or a `variant-picker` is missing.
   *
   * @param {Document} newHtml
   * @returns {void}
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }
  handleClick = async (event) => {
    event.preventDefault();
    if (this.#isLoading) return;
    const currentUrl = this.productPageUrl;
    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }
    let productGrid = this.#cachedContent.get(currentUrl);
    this.#isLoading = true;
    try {
      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            productGrid = gridElement.cloneNode(true);
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }
      if (productGrid) {
        const freshContent = productGrid.cloneNode(true);
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[quick-add] Failed to load product page:', error);
      }
      return;
    } finally {
      this.#isLoading = false;
    }
    this.#openQuickAddModal();
  };
  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector(
      '.product-information__media',
    );
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true },
    );
  }
  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), {
      once: true,
    });
  };
  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    try {
      const response = await fetch(productPageUrl, {
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === controller) {
        this.#abortController = null;
      }
    }
  }
  /**
   * Public entrypoint: re-render the quick-add modal contents with `productGrid`.
   *
   * @param {Document | Element} productGrid Freshly fetched grid fragment.
   * @returns {Promise<void>}
   */ async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;
    if (isMobileBreakpoint()) {
      this.#rearrangeQuickAddMobile(productGrid);
    }
    this.#morphQuickAdd(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }
  /**
   * Re-arrange `productGrid` children for the mobile layout: title + price
   * header on top, then variant picker and product form. Mirrors the original
   * inline behavior; the existing `product-details` block is removed.
   *
   * @param {Document | Element} productGrid
   * @returns {void}
   */ #rearrangeQuickAddMobile(productGrid) {
    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector(
      'product-form-component',
    );
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const productTitle = document.createElement('a');
    productTitle.textContent = this.dataset.productTitle || '';
    productTitle.href = this.productPageUrl;
    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    productHeader.appendChild(productTitle);
    if (productPrice) {
      productHeader.appendChild(productPrice);
    }
    productGrid.appendChild(productHeader);
    if (variantPicker) {
      productGrid.appendChild(variantPicker);
    }
    if (productFormComponent) {
      productGrid.appendChild(productFormComponent);
    }
    productDetails?.remove();
  }
  /**
   * Apply the `view-event-payload` attribute and morph `modalContent` from
   * `productGrid`.
   *
   * @param {HTMLElement} modalContent
   * @param {Document | Element} productGrid
   * @returns {void}
   */ #morphQuickAdd(modalContent, productGrid) {
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);
    morph(modalContent, productGrid);
  }
  /**
   * @param {HTMLElement} modalContent
   * @returns {void}
   */ #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    const modalInputs = modalContent.querySelectorAll(
      'input[type="radio"][data-variant-id]',
    );
    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId === selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}
if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      { signal: this.#abortController.signal },
    );
    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
    );
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
    );
  }
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail: detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError')
          console.warn('[quick-add] Event promise rejected:', error);
      });
  };
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail: detail }) => {
        if (!detail?.html) return;
        this.#syncProductTitleHrefs(detail.html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError')
          console.warn('[quick-add] Event promise rejected:', error);
      });
  };
  /**
   * Mirror the chosen variant's product title href onto the quick-add dialog
   * links (desktop "view-product-title" and mobile "product-header").
   *
   * @returns {void}
   */
  #syncProductTitleHrefs(html) {
    const anchorElement = html.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');
    if (anchorElement && viewMoreDetailsLink)
      viewMoreDetailsLink.href = anchorElement.href;
    if (anchorElement && mobileProductTitle)
      mobileProductTitle.href = anchorElement.href;
  }
  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    )
      return;
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          if (this.isConnected) grid.style.width = '';
        });
      }
    });
  };
}
if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
